import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { ModuleWithProviders, NgModule, inject, provideAppInitializer } from "@angular/core";
import { JWT_OPTIONS, JwtHelperService } from "@auth0/angular-jwt";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { PcpHttpModule } from "@pcp/http";
import { CodemeterModule } from "@pcp/codemeter";
import { IsAuthenticatedDirective } from "./directives/is-authenticated.directive";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { PersistentTokenStorageService } from "./services/persistent-token-storage.service";
import { authFeatureKey, reducer } from "./store/auth.reducer";
import { PcpAuthService } from "./services/pcp-auth.service";
import { AuthEffects } from "./store/auth.effects";
import { SasProvider } from "./services/sas-provider";
import { MatDialogModule } from "@angular/material/dialog";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { AUTH_INTERCEPTOR_IGNORE_DOMAINS, BROADCAST_USER_LOGOUT, PCP_SHARED_SAS, TOKEN_STORAGE_ADAPTER } from "./injection-tokens";


export function initAuthState(data: PersistentTokenStorageService, authService: PcpAuthService) {
    // save function to a variable, so it won't be inlined in production
    // noinspection UnnecessaryLocalVariableJS
    const initializer = async () => {
        if (window.location.href.includes('login')) {
            authService.markAsInitialized();
            return;
        }
        try {
            return await authService.restoreToken();
        } catch (error) {
            authService.markAsInitialized();
            throw error;
        }
    };

    return initializer;
}

export function initAuthStateForLiveLessons(data: PersistentTokenStorageService, authService: PcpAuthService) {
    // save function to a variable, so it won't be inlined in production
    // noinspection UnnecessaryLocalVariableJS
    const initializer = async () => {
        try {
            return await authService.restoreLiveLessonToken();
        } catch (error) {
            authService.markAsInitialized();
            throw error;
        }
    };

    return initializer;
}

type AuthModuleConfig = {
    /** persist to localStorage or sessionStorage */
    tokenStorageAdapter: Storage;

    /**
     * If true, use a DataChannel to broadcast use logouts to other browser tabs in order to log them out, too.
     *
     * NOTE: this is a workaround to prevent particular client types - mainly beamers - from being logged out unintentionally.
     * E.g. a user/browser could have opened two beamer tabs from two different lessons.
     * -> if one lesson gets stopped and logged out, the other one would be logged out, too even if the lesson is not stopped.
     */
    broadcastLogout?: boolean;

    interceptorIgnoreDomains?: string[];
};

@NgModule({
    providers: [
        { provide: JwtHelperService },
        { provide: BROADCAST_USER_LOGOUT, useValue: false },
    ],
    imports: [
        CommonModule,
        StoreModule.forFeature(authFeatureKey, reducer),
        EffectsModule.forFeature([AuthEffects]),
        CodemeterModule,
        PcpHttpModule,
        MatDialogModule,
        MatSnackBarModule,
        IsAuthenticatedDirective,
    ],
    exports: [
        IsAuthenticatedDirective,
    ]
})
export class PcpAuthModule {
    public static forRoot(config: AuthModuleConfig): ModuleWithProviders<PcpAuthModule> {
        return {
            ngModule: PcpAuthModule,
            providers: [
                { provide: JWT_OPTIONS, useValue: {} },
                provideAppInitializer(() => {
        const initializerFn = (initAuthState)(inject(PersistentTokenStorageService), inject(PcpAuthService));
        return initializerFn();
      }),
                { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
                {
                    provide: PCP_SHARED_SAS,
                    deps: [SasProvider],
                    useFactory: (provider: SasProvider) => provider.sas$,
                },
                { provide: TOKEN_STORAGE_ADAPTER, useValue: config.tokenStorageAdapter },
                { provide: BROADCAST_USER_LOGOUT, useValue: config.broadcastLogout },
                { provide: AUTH_INTERCEPTOR_IGNORE_DOMAINS, useValue: config.interceptorIgnoreDomains ?? [] },
            ]
        }
    }

    public static forLiveLesson(config: AuthModuleConfig): ModuleWithProviders<PcpAuthModule> {
        return {
            ngModule: PcpAuthModule,
            providers: [
                { provide: JWT_OPTIONS, useValue: {} },
                provideAppInitializer(() => {
        const initializerFn = (initAuthStateForLiveLessons)(inject(PersistentTokenStorageService), inject(PcpAuthService));
        return initializerFn();
      }),
                { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
                {
                    provide: PCP_SHARED_SAS,
                    deps: [SasProvider],
                    useFactory: (provider: SasProvider) => provider.sas$,
                },
                { provide: TOKEN_STORAGE_ADAPTER, useValue: config.tokenStorageAdapter },
                { provide: AUTH_INTERCEPTOR_IGNORE_DOMAINS, useValue: config.interceptorIgnoreDomains ?? [] },
            ]
        }
    }
}
